/**
 * Monthly Bills — recurring bill tracker
 * Features: List + Calendar views, category colors, pay URLs, partial payments,
 * one-tap toggle, bulk select, grace-period countdown chips, edits, history.
 */
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, Check, X, Plus, Edit2, Trash2, AlertTriangle, ExternalLink,
  CheckCircle2, DollarSign, Calendar as CalendarIcon, Mail, RefreshCw,
  ChevronDown, ChevronUp, List, Grid3x3, Clock, Layers, CheckSquare, Square,
  Sparkles, TrendingUp, Filter, Zap, Flame, Coins, Wallet, Search,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import EmailAutomationToggles from "../components/EmailAutomationToggles";

const API = process.env.REACT_APP_BACKEND_URL;
const GRACE_DAYS = 10;

const fmt = (n) =>
  `$${(Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const ordinal = (n) => {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

// ─── Category styling ────────────────────────────────────────────────────────
const CATEGORIES = {
  auto:    { label: "Auto",          dot: "bg-blue-400",    text: "text-blue-300",    border: "border-blue-500/40",    glow: "bg-blue-500/10" },
  card:    { label: "Card",          dot: "bg-purple-400",  text: "text-purple-300",  border: "border-purple-500/40",  glow: "bg-purple-500/10" },
  loan:    { label: "Personal Loan", dot: "bg-amber-400",   text: "text-amber-300",   border: "border-amber-500/40",   glow: "bg-amber-500/10" },
  utility: { label: "Utility",       dot: "bg-cyan-400",    text: "text-cyan-300",    border: "border-cyan-500/40",    glow: "bg-cyan-500/10" },
  other:   { label: "Other",         dot: "bg-zinc-400",    text: "text-zinc-300",    border: "border-zinc-600",       glow: "bg-zinc-700/30" },
};

// ─── Grace-period countdown ──────────────────────────────────────────────────
function statusChip(bill, today = new Date()) {
  if (bill.status.paid_in_full) return null;
  const due = new Date(today.getFullYear(), today.getMonth(), bill.day);
  const graceEnd = new Date(today.getFullYear(), today.getMonth(), bill.day + GRACE_DAYS);
  const daysToDue = Math.round((due - new Date(today.getFullYear(), today.getMonth(), today.getDate())) / 86400000);
  const daysToGrace = Math.round((graceEnd - new Date(today.getFullYear(), today.getMonth(), today.getDate())) / 86400000);

  if (daysToGrace < 0) {
    return { label: `Overdue ${Math.abs(daysToGrace)}d`, cls: "bg-red-500/20 text-red-300 border-red-500/40", icon: AlertTriangle };
  }
  if (daysToDue < 0) {
    // past due, but within grace
    return { label: `Grace ends in ${daysToGrace}d`, cls: "bg-amber-500/20 text-amber-300 border-amber-500/40", icon: Clock };
  }
  if (daysToDue === 0) {
    return { label: "Due today", cls: "bg-yellow-500/20 text-yellow-300 border-yellow-500/40", icon: Clock };
  }
  return { label: `Due in ${daysToDue}d`, cls: "bg-zinc-700/40 text-zinc-300 border-zinc-600", icon: Clock };
}

export default function MonthlyBillsPage() {
  const [data, setData] = useState(null);
  const [nextData, setNextData] = useState(null);
  const [previousMonths, setPreviousMonths] = useState([]); // [oldest, …, newest] each = { month_key, month_label, bills, summary }
  const [loadingPrev, setLoadingPrev] = useState(false);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("list"); // 'list' | 'calendar'
  const [showAddModal, setShowAddModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [paymentFor, setPaymentFor] = useState(null);
  const [expandedHistory, setExpandedHistory] = useState({});
  const [selected, setSelected] = useState(new Set());
  const [bulkMode, setBulkMode] = useState(false);
  const [filter, setFilter] = useState("all"); // 'all' | 'unpaid' | 'overdue' | 'paid'
  const [searchTerm, setSearchTerm] = useState("");
  const [buildInfo, setBuildInfo] = useState(null);
  // Track in-flight silent refetches so we don't stack them and cause thrash.
  const refetchInFlight = useRef(false);

  // ── Fetch: two modes ─────────────────────────────────────────────────
  // silent=true  → does NOT flip the page into "Loading…" (used for post-
  //                mutation refreshes so the current UI stays visible).
  // silent=false → sets loading, used only on the initial mount.
  const fetchData = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const cur = await axios.get(`${API}/api/monthly-bills`);
      setData((prev) => {
        if (cur.data.just_reset_from && prev?.month_key === cur.data.just_reset_from) {
          toast.success(`🎉 All ${cur.data.just_reset_from} bills paid! Now showing ${cur.data.month_label}.`);
        }
        return cur.data;
      });
      // Pre-load next month preview (non-blocking for UX).
      const [y, m] = cur.data.month_key.split("-").map(Number);
      const nm = m === 12 ? `${y + 1}-01` : `${y}-${String(m + 1).padStart(2, "0")}`;
      try {
        const nxt = await axios.get(`${API}/api/monthly-bills?month=${nm}`);
        setNextData(nxt.data);
      } catch (_) { /* next month is optional */ }
      if (!silent) setSelected(new Set());
    } catch (e) {
      if (!silent) toast.error("Failed to load monthly bills");
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // Initial load on mount. Generic disable below covers both the
  // exhaustive-deps lint (deps array intentionally empty) and the React 19
  // `set-state-in-effect` lint (data fetch on mount is the standard pattern).
  useEffect(() => {
    // eslint-disable-next-line
    fetchData(false);
    // Fetch build info once so we can show a visible "Preview build" chip
    // on this page — this lets you eyeball whether production is running
    // the latest deploy without digging through the sidebar.
    axios.get(`${API}/api/version`, { timeout: 8000 })
      .then((r) => setBuildInfo(r.data))
      .catch(() => setBuildInfo({ build_date: "offline" }));
  }, []);

  // ── Previous-month history ─────────────────────────────────────────────
  // Pulls the month before the oldest already-loaded month (or before the
  // current month) and prepends it so users can scroll up through history.
  // Plain async handler (not useEffect) — called from the button onClick.
  const loadPreviousMonth = async () => {
    if (loadingPrev || !data) return;
    setLoadingPrev(true);
    try {
      const oldestKey = previousMonths.length ? previousMonths[0].month_key : data.month_key;
      const [y, m] = oldestKey.split("-").map(Number);
      const pm = m === 1 ? `${y - 1}-12` : `${y}-${String(m - 1).padStart(2, "0")}`;
      const r = await axios.get(`${API}/api/monthly-bills?month=${pm}`);
      setPreviousMonths((prev) => [r.data, ...prev]);
    } catch (e) {
      toast.error("Failed to load previous month");
    } finally {
      setLoadingPrev(false);
    }
  };

  // Re-fetch every already-loaded historical month so the UI reflects any
  // mutation (toggle-paid, payment, bill edit) made on a past-month row.
  // Without this, history blocks go stale because fetchData() only refreshes
  // the CURRENT and NEXT month docs.
  const refreshPreviousMonths = async () => {
    if (previousMonths.length === 0) return;
    try {
      const r = await Promise.all(
        previousMonths.map((pm) =>
          axios.get(`${API}/api/monthly-bills?month=${pm.month_key}`).then((res) => res.data)
        )
      );
      setPreviousMonths(r);
    } catch (e) {
      // Silent failure — main fetch still succeeded, user can hard-refresh.
    }
  };

  // Wraps fetchData() + refreshPreviousMonths() — call after every mutation
  // so both the current month and any loaded history stay in sync.
  // SILENT — never flips the page into a full-screen "Loading…" spinner.
  const refetchAll = async () => {
    if (refetchInFlight.current) return;
    refetchInFlight.current = true;
    try {
      await Promise.all([fetchData(true), refreshPreviousMonths()]);
    } finally {
      refetchInFlight.current = false;
    }
  };

  // Helper: patch a single bill's status in local state WITHOUT waiting for
  // the network round-trip.  Used by optimistic toggle/payment updates so the
  // UI feels instant and never appears "stuck".
  const patchBillStatus = (billId, monthKey, statusPatch) => {
    const patch = (obj) => {
      if (!obj) return obj;
      const nextBills = obj.bills?.map((b) =>
        b.id === billId ? { ...b, status: { ...b.status, ...statusPatch } } : b
      ) || obj.bills;
      const totalDue = nextBills.reduce((s, b) => s + (Number(b.amount) || 0), 0);
      const totalPaid = nextBills.reduce((s, b) => s + (Number(b.status?.paid_total) || 0), 0);
      const paidCount = nextBills.filter((b) => b.status?.paid_in_full).length;
      const totalBills = nextBills.length;
      return {
        ...obj,
        bills: nextBills,
        summary: {
          ...obj.summary,
          total_due: totalDue,
          total_paid: totalPaid,
          total_remaining: Math.max(0, totalDue - totalPaid),
          paid_count: paidCount,
          unpaid_count: totalBills - paidCount,
          total_bills: totalBills,
          all_paid: paidCount === totalBills && totalBills > 0,
        },
      };
    };
    if (data?.month_key === monthKey) setData((d) => patch(d));
    else if (nextData?.month_key === monthKey) setNextData((d) => patch(d));
    else setPreviousMonths((arr) => arr.map((pm) => pm.month_key === monthKey ? patch(pm) : pm));
  };

  // Set of bill IDs currently mid-toggle so we can lock the button and
  // prevent double-taps from producing the "click looks like it took but
  // doesn't stick" flip-flop the user reported (Feb 2026).
  const [toggling, setToggling] = useState(() => new Set());

  const toggle = async (bill, monthKey) => {
    // Feb 2026 — defensive: coerce bill.id to a string and short-circuit
    // any bill without a resolvable id.  Previously if `bill.id` was
    // undefined (legacy Mongo doc, hydration race, etc.) we'd add
    // `undefined` to the toggling Set and every subsequent toggle click
    // on ANY bill would short-circuit via `toggling.has(undefined)===true`.
    // This is what caused specific bills (best egg last time, GMC now) to
    // "look tapped but never change" while other bills kept working.
    const billId = bill && bill.id != null ? String(bill.id) : null;
    if (!billId) {
      toast.error(`Cannot toggle "${bill?.name || 'bill'}" — missing bill ID.  Try deleting and re-adding the bill.`, { duration: 4000 });
      return;
    }
    if (toggling.has(billId)) return;
    setToggling((prev) => new Set(prev).add(billId));
    // Compute the DESIRED state up front and send it explicitly to the
    // backend as ?paid=… — makes the endpoint idempotent so re-clicks
    // resolve to a single deterministic state instead of flipping based
    // on whatever was last read from Mongo.
    const wasPaid = !!bill.status?.paid_in_full;
    const targetPaid = !wasPaid;

    // OPTIMISTIC UPDATE — patch local state instantly so the checkmark
    // flips before the network call even lands.  This is what makes the
    // toggle feel snappy and eliminates the "loading then reverted" bug.
    const optimisticStatus = targetPaid
      ? { paid_in_full: true, paid_total: bill.amount, paid_full_at: new Date().toISOString() }
      : { paid_in_full: false, paid_total: 0, payments: [], paid_full_at: null };
    patchBillStatus(billId, monthKey, optimisticStatus);

    try {
      const params = new URLSearchParams();
      if (monthKey) params.set("month", monthKey);
      params.set("paid", String(targetPaid));
      await axios.post(`${API}/api/monthly-bills/${encodeURIComponent(billId)}/toggle-paid?${params.toString()}`);
      if (targetPaid) toast.success(`${bill.name} marked paid`, { duration: 1500 });
      // Silent background sync — pulls authoritative state without unmounting the page.
      refetchAll();
    } catch (e) {
      // Roll back the optimistic change on error.
      patchBillStatus(billId, monthKey, {
        paid_in_full: wasPaid,
        paid_total: bill.status?.paid_total || 0,
        paid_full_at: bill.status?.paid_full_at || null,
      });
      // Surface the actual reason so stuck bills can be diagnosed.
      const detail = e?.response?.data?.detail || e?.response?.statusText || e?.message || 'Network error';
      const status = e?.response?.status;
      toast.error(`Failed to update ${bill.name}${status ? ` (${status})` : ''}: ${detail}`, { duration: 4000 });
    } finally {
      setToggling((prev) => {
        const next = new Set(prev);
        next.delete(billId);
        return next;
      });
    }
  };

  const bulkMarkPaid = async () => {
    if (selected.size === 0) return;
    try {
      const unpaidSelected = data.bills.filter((b) => selected.has(b.id) && !b.status.paid_in_full);
      // Optimistic: patch all selected bills paid immediately.
      unpaidSelected.forEach((b) =>
        patchBillStatus(b.id, data.month_key, {
          paid_in_full: true,
          paid_total: b.amount,
          paid_full_at: new Date().toISOString(),
        })
      );
      await Promise.all(unpaidSelected.map((b) =>
        axios.post(`${API}/api/monthly-bills/${b.id}/toggle-paid?paid=true&month=${data.month_key}`)
      ));
      toast.success(`Marked ${unpaidSelected.length} bill${unpaidSelected.length === 1 ? "" : "s"} paid`);
      setSelected(new Set());
      setBulkMode(false);
      refetchAll();
    } catch (e) {
      toast.error("Failed to bulk mark paid");
      refetchAll(); // reconcile
    }
  };

  const submitPayment = async () => {
    if (!paymentFor) return;
    const amt = parseFloat(paymentFor.amount);
    if (!amt || amt <= 0) { toast.error("Enter a payment amount"); return; }
    try {
      const qs = paymentFor.monthKey ? `?month=${paymentFor.monthKey}` : "";
      await axios.post(`${API}/api/monthly-bills/${paymentFor.bill.id}/payments${qs}`, {
        amount: amt, notes: paymentFor.notes || "",
      });
      toast.success(`Payment of ${fmt(amt)} added to ${paymentFor.bill.name}`);
      setPaymentFor(null);
      await refetchAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to add payment"); }
  };

  const deletePayment = async (billId, paymentId, monthKey) => {
    if (!window.confirm("Remove this payment?")) return;
    try {
      const qs = monthKey ? `?month=${monthKey}` : "";
      await axios.delete(`${API}/api/monthly-bills/${billId}/payments/${paymentId}${qs}`);
      await refetchAll();
    } catch (e) { toast.error("Failed to remove payment"); }
  };

  const saveBill = async (payload) => {
    try {
      if (editing && editing.id) {
        await axios.put(`${API}/api/monthly-bills/${editing.id}`, payload);
        toast.success("Bill updated");
      } else {
        await axios.post(`${API}/api/monthly-bills`, payload);
        toast.success(`${payload.name} added`);
      }
      setEditing(null);
      setShowAddModal(false);
      await refetchAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to save bill"); }
  };

  const deleteBill = async (bill) => {
    if (!window.confirm(`Remove "${bill.name}" from the monthly list? Past payment history for this bill will also be removed.`)) return;
    try {
      await axios.delete(`${API}/api/monthly-bills/${bill.id}`);
      toast.success(`${bill.name} removed`);
      await refetchAll();
    } catch (e) { toast.error("Failed to remove bill"); }
  };

  const runReminders = async () => {
    try {
      const r = await axios.post(`${API}/api/monthly-bills/run-reminders`);
      const d = r.data || {};
      if (d.reason) { toast.error(d.reason); return; }
      if (d.sent > 0) {
        const stages = (d.sent_bills || []).map((b) => b.name).join(", ");
        toast.success(`${d.sent} reminder${d.sent === 1 ? "" : "s"} sent to ${d.sent_to}${stages ? ` — ${stages}` : ""}`);
      } else {
        toast.info(`No bills hit a reminder stage today (${d.skipped || 0} checked)`);
      }
    } catch (e) { toast.error("Failed to run reminders"); }
  };

  const sendTestReminder = async () => {
    try {
      const r = await axios.post(`${API}/api/monthly-bills/test-reminder`);
      const d = r.data || {};
      if (d.success) toast.success(`Test reminder sent to ${d.sent_to} (${d.bill})`);
      else toast.error(d.reason || "Test reminder failed");
    } catch (e) { toast.error("Failed to send test reminder"); }
  };

  const forceAdvance = async () => {
    if (!window.confirm("Manually advance to next month? Bills you haven't paid will stay logged for the current month.")) return;
    try {
      const r = await axios.post(`${API}/api/monthly-bills/force-advance`);
      toast.success(`Now viewing ${r.data.month_label}`);
      await fetchData(true);
    } catch (e) { toast.error("Failed to advance month"); }
  };

  // ── Jump to a specific past month ──────────────────────────────────────
  // Loads every month between the picked month and the oldest-already-loaded
  // month in one go, so the user doesn't have to click "Show previous month"
  // 12 times to see January.
  const jumpToMonth = async (targetKey) => {
    if (!targetKey || !data || loadingPrev) return;
    const oldestKey = previousMonths.length ? previousMonths[0].month_key : data.month_key;
    if (targetKey >= oldestKey) return; // already loaded
    try {
      setLoadingPrev(true);
      // Build the list of keys from oldest desired month up to (but excluding) oldestKey
      const toFetch = [];
      let cursor = targetKey;
      while (cursor < oldestKey) {
        toFetch.push(cursor);
        const [y, m] = cursor.split("-").map(Number);
        cursor = m === 12 ? `${y + 1}-01` : `${y}-${String(m + 1).padStart(2, "0")}`;
      }
      const results = await Promise.all(
        toFetch.map((mk) => axios.get(`${API}/api/monthly-bills?month=${mk}`).then((r) => r.data))
      );
      // results is already in chronological order (oldest first) — prepend as a block
      setPreviousMonths((prev) => [...results, ...prev]);
      // Scroll to the top so the new history block is visible
      setTimeout(() => window.scrollTo({ top: 200, behavior: "smooth" }), 250);
    } catch (e) {
      toast.error("Failed to load that month");
    } finally {
      setLoadingPrev(false);
    }
  };

  // Build a 24-month list for the dropdown (oldest → newest, excluding the
  // current month and any already-loaded historical months so the picker
  // only offers months you haven't loaded yet).
  const monthOptions = useMemo(() => {
    if (!data) return [];
    const opts = [];
    const [cy, cm] = data.month_key.split("-").map(Number);
    for (let i = 1; i <= 24; i++) {
      let y = cy, m = cm - i;
      while (m <= 0) { m += 12; y -= 1; }
      const key = `${y}-${String(m).padStart(2, "0")}`;
      const label = new Date(y, m - 1, 1).toLocaleString("en-US", { month: "long", year: "numeric" });
      opts.push({ key, label });
    }
    const loadedKeys = new Set(previousMonths.map((p) => p.month_key));
    return opts.filter((o) => !loadedKeys.has(o.key));
  }, [data, previousMonths]);

  const toggleSelect = (id) => {
    setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };

  // ── Apply filter + search to the current-month bills. Filter chips let
  // the user quickly slice by status; search matches on bill name / notes.
  // Hooks must run BEFORE any early return per rules-of-hooks.
  const bills = data?.bills || [];
  const filteredBills = useMemo(() => {
    let list = bills;
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase().trim();
      list = list.filter((b) =>
        (b.name || "").toLowerCase().includes(q) ||
        (b.notes || "").toLowerCase().includes(q)
      );
    }
    if (filter === "paid") list = list.filter((b) => b.status?.paid_in_full);
    else if (filter === "unpaid") list = list.filter((b) => !b.status?.paid_in_full);
    else if (filter === "overdue") {
      const today = new Date();
      list = list.filter((b) => {
        if (b.status?.paid_in_full) return false;
        const graceEnd = new Date(today.getFullYear(), today.getMonth(), b.day + GRACE_DAYS);
        return graceEnd < new Date(today.getFullYear(), today.getMonth(), today.getDate());
      });
    }
    return list;
  }, [bills, filter, searchTerm]);

  const chipCounts = useMemo(() => {
    const today = new Date();
    let overdue = 0;
    for (const b of bills) {
      if (b.status?.paid_in_full) continue;
      const graceEnd = new Date(today.getFullYear(), today.getMonth(), b.day + GRACE_DAYS);
      if (graceEnd < new Date(today.getFullYear(), today.getMonth(), today.getDate())) overdue += 1;
    }
    const paid = bills.filter((b) => b.status?.paid_in_full).length;
    return { all: bills.length, unpaid: bills.length - paid, overdue, paid };
  }, [bills]);

  // Only block the whole page on the INITIAL load.  Subsequent refetches
  // (post-mutation) use silent mode, so this branch never fires again and
  // the toggle-then-Loading-then-revert bug is impossible.
  if (!data) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center text-zinc-400" data-testid="mb-initial-loading">
        <div className="flex items-center gap-3">
          <div className="w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
          <span className="text-sm">Loading your bills…</span>
        </div>
      </div>
    );
  }

  const { month_label, summary } = data;

  return (
    <div className="min-h-screen bg-gradient-to-b from-zinc-950 via-zinc-950 to-black text-white">
      {/* Header */}
      <div className="border-b border-zinc-800/80 bg-gradient-to-r from-emerald-900/20 via-zinc-950/90 to-teal-900/20 px-4 sm:px-6 py-3 sticky top-0 z-30 backdrop-blur-lg">
        <div className="max-w-6xl mx-auto flex items-center gap-2 flex-wrap">
          <Link to="/" className="flex items-center gap-1.5 text-zinc-400 hover:text-white text-sm transition-colors">
            <ArrowLeft className="w-4 h-4" /> Dashboard
          </Link>

          {/* PROMINENT BUILD PILL — so you can visually confirm production
              matches preview after a deploy.  If this pill shows an older
              date than the preview, the deploy didn't sync yet. */}
          {buildInfo && (
            <span
              className="ml-2 inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px] font-mono font-bold bg-emerald-500/15 border border-emerald-500/40 text-emerald-300"
              title={`Backend build: ${buildInfo.iso || buildInfo.build_date}`}
              data-testid="mb-build-pill"
            >
              <Sparkles className="w-3 h-3" />
              v{buildInfo.build_date}
            </span>
          )}

          {/* View toggle */}
          <div className="flex items-center gap-1 ml-2 bg-zinc-900/80 border border-zinc-700 rounded-lg p-0.5">
            <button
              onClick={() => setView("list")}
              className={`px-3 py-1 rounded-md text-xs font-medium flex items-center gap-1 transition-all ${view === "list" ? "bg-emerald-600 text-white shadow" : "text-zinc-400 hover:text-white"}`}
              data-testid="view-list-btn"
            >
              <List className="w-3.5 h-3.5" /> List
            </button>
            <button
              onClick={() => setView("calendar")}
              className={`px-3 py-1 rounded-md text-xs font-medium flex items-center gap-1 transition-all ${view === "calendar" ? "bg-emerald-600 text-white shadow" : "text-zinc-400 hover:text-white"}`}
              data-testid="view-calendar-btn"
            >
              <Grid3x3 className="w-3.5 h-3.5" /> Calendar
            </button>
          </div>

          {/* Jump-to-month picker */}
          {view === "list" && monthOptions.length > 0 && (
            <div className="relative ml-1">
              <select
                value=""
                onChange={(e) => { jumpToMonth(e.target.value); e.target.value = ""; }}
                disabled={loadingPrev}
                className="appearance-none bg-zinc-900 border border-zinc-700 text-zinc-200 text-xs font-medium rounded-lg pl-8 pr-7 py-1.5 hover:bg-zinc-800 focus:outline-none focus:ring-1 focus:ring-emerald-500 cursor-pointer disabled:opacity-50"
                data-testid="month-picker"
                title="Jump to a past month"
              >
                <option value="" disabled>
                  {loadingPrev ? "Loading…" : "Jump to month…"}
                </option>
                {monthOptions.map((o) => (
                  <option key={o.key} value={o.key} className="bg-zinc-900 text-zinc-200">
                    {o.label}
                  </option>
                ))}
              </select>
              <CalendarIcon className="w-3.5 h-3.5 text-emerald-400 absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none" />
              <ChevronDown className="w-3.5 h-3.5 text-zinc-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
            </div>
          )}

          <div className="flex-1" />

          {view === "list" && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setBulkMode((b) => !b); setSelected(new Set()); }}
              className={`bg-zinc-900 border-zinc-700 hover:bg-zinc-800 text-zinc-200 ${bulkMode ? "ring-1 ring-emerald-500" : ""}`}
              data-testid="bulk-toggle-btn"
            >
              <Layers className="w-4 h-4 mr-1" /> {bulkMode ? "Done" : "Select"}
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={sendTestReminder} className="bg-zinc-900 border-zinc-700 hover:bg-zinc-800 text-zinc-200" data-testid="test-reminder-btn" title="Send a test reminder email right now to verify delivery">
            <Mail className="w-4 h-4 mr-1" /> Test email
          </Button>
          <Button variant="outline" size="sm" onClick={runReminders} className="bg-zinc-900 border-zinc-700 hover:bg-zinc-800 text-zinc-200" data-testid="run-reminders-btn">
            <Mail className="w-4 h-4 mr-1" /> Run reminders
          </Button>
          <Button size="sm" onClick={() => { setEditing(null); setShowAddModal(true); }} className="bg-emerald-600 hover:bg-emerald-700 shadow-lg shadow-emerald-500/20" data-testid="add-bill-btn">
            <Plus className="w-4 h-4 mr-1" /> Add bill
          </Button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-5">
        {/* ── HERO: Big month header + circular progress + hot-stat cards ── */}
        <HeroPanel
          monthLabel={month_label}
          summary={summary}
          bills={bills}
          onForceAdvance={forceAdvance}
        />

        {/* ── Email Automations ── */}
        <EmailAutomationToggles category="monthly_bills" />

        {/* Filter chips + search — quick slice-and-dice tools that were the #1
            missing feature per user feedback (Feb 2026 revamp). */}
        <div className="rounded-2xl border border-zinc-800/80 bg-zinc-900/40 backdrop-blur p-3 flex items-center gap-2 flex-wrap">
          <FilterChip
            active={filter === "all"}
            onClick={() => setFilter("all")}
            icon={<Layers className="w-3.5 h-3.5" />}
            label="All"
            count={chipCounts.all}
            testId="filter-all"
            tone="zinc"
          />
          <FilterChip
            active={filter === "unpaid"}
            onClick={() => setFilter("unpaid")}
            icon={<Clock className="w-3.5 h-3.5" />}
            label="Unpaid"
            count={chipCounts.unpaid}
            testId="filter-unpaid"
            tone="amber"
          />
          <FilterChip
            active={filter === "overdue"}
            onClick={() => setFilter("overdue")}
            icon={<Flame className="w-3.5 h-3.5" />}
            label="Overdue"
            count={chipCounts.overdue}
            testId="filter-overdue"
            tone="red"
          />
          <FilterChip
            active={filter === "paid"}
            onClick={() => setFilter("paid")}
            icon={<CheckCircle2 className="w-3.5 h-3.5" />}
            label="Paid"
            count={chipCounts.paid}
            testId="filter-paid"
            tone="emerald"
          />
          <div className="flex-1 min-w-[140px]" />
          <div className="relative flex-1 min-w-[160px] max-w-xs">
            <Search className="w-3.5 h-3.5 text-zinc-500 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="text"
              placeholder="Search bills…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-zinc-900 border border-zinc-700 text-zinc-200 text-xs rounded-lg pl-8 pr-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-emerald-500 placeholder-zinc-500"
              data-testid="mb-search-input"
            />
          </div>
        </div>

        {/* Bulk action bar */}
        {bulkMode && selected.size > 0 && (
          <div className="sticky top-[68px] z-20 rounded-xl border border-emerald-500/40 bg-emerald-500/10 backdrop-blur p-3 flex items-center gap-3">
            <CheckSquare className="w-5 h-5 text-emerald-300" />
            <span className="text-sm text-emerald-200">{selected.size} selected</span>
            <div className="flex-1" />
            <Button size="sm" onClick={bulkMarkPaid} className="bg-emerald-600 hover:bg-emerald-700" data-testid="bulk-mark-paid-btn">
              <Check className="w-4 h-4 mr-1" /> Mark paid
            </Button>
            <Button size="sm" variant="outline" onClick={() => setSelected(new Set())} className="bg-zinc-900 border-zinc-700 hover:bg-zinc-800">
              Clear
            </Button>
          </div>
        )}

        {/* MAIN CONTENT */}
        {view === "list" ? (
          <>
            {/* Previous-month history (scroll-up) */}
            <div className="flex items-center gap-3 mb-1">
              <div className="h-px flex-1 bg-zinc-800" />
              <Button
                variant="outline"
                size="sm"
                onClick={loadPreviousMonth}
                disabled={loadingPrev}
                className="bg-zinc-900 border-zinc-700 hover:bg-zinc-800 text-zinc-200 text-xs"
                data-testid="load-prev-month-btn"
              >
                <ChevronUp className="w-3.5 h-3.5 mr-1" />
                {loadingPrev
                  ? "Loading…"
                  : previousMonths.length
                    ? `Load month before ${previousMonths[0].month_label}`
                    : "Show previous month"}
              </Button>
              <div className="h-px flex-1 bg-zinc-800" />
            </div>

            {previousMonths.map((pm) => (
              <div key={pm.month_key} className="pb-2">
                <div className="flex items-center gap-3 mt-4 mb-3">
                  <div className="h-px flex-1 bg-zinc-800" />
                  <p className="text-xs uppercase tracking-widest text-zinc-500 font-bold flex items-center gap-2">
                    <CalendarIcon className="w-3.5 h-3.5" />
                    History · {pm.month_label}
                    <span className="text-emerald-400 font-mono normal-case tracking-normal">
                      {pm.summary.paid_count}/{pm.summary.total_bills} paid · {fmt(pm.summary.total_paid)}
                    </span>
                  </p>
                  <div className="h-px flex-1 bg-zinc-800" />
                </div>
                <div className="opacity-95">
                  <ListView
                    bills={pm.bills}
                    monthKey={pm.month_key}
                    monthLabel={pm.month_label}
                    bulkMode={false}
                    selected={new Set()}
                    toggleSelect={() => {}}
                    onToggle={(b) => toggle(b, pm.month_key)}
                    onPay={(b) => setPaymentFor({ bill: b, monthKey: pm.month_key, amount: Math.max(0, b.amount - b.status.paid_total).toFixed(2), notes: "" })}
                    onEdit={(b) => { setEditing(b); setShowAddModal(true); }}
                    onDelete={deleteBill}
                    expandedHistory={expandedHistory}
                    setExpandedHistory={setExpandedHistory}
                    onDeletePayment={(billId, paymentId) => deletePayment(billId, paymentId, pm.month_key)}
                    historical
                  />
                </div>
              </div>
            ))}

            {previousMonths.length > 0 && (
              <div className="flex items-center gap-3 mt-3 mb-2">
                <div className="h-px flex-1 bg-emerald-500/20" />
                <p className="text-xs uppercase tracking-widest text-emerald-400 font-bold">
                  Current · {month_label}
                </p>
                <div className="h-px flex-1 bg-emerald-500/20" />
              </div>
            )}

            {/* Feb 2026 UX revamp — hero panel is now above filter chips.
                Filter chips + search apply to the current month bills. */}
            <ListView
              bills={filteredBills}
              monthKey={data.month_key}
              monthLabel={month_label}
              bulkMode={bulkMode}
              selected={selected}
              toggleSelect={toggleSelect}
              onToggle={(b) => toggle(b, data.month_key)}
              onPay={(b) => setPaymentFor({ bill: b, monthKey: data.month_key, amount: Math.max(0, b.amount - b.status.paid_total).toFixed(2), notes: "" })}
              onEdit={(b) => { setEditing(b); setShowAddModal(true); }}
              onDelete={deleteBill}
              expandedHistory={expandedHistory}
              setExpandedHistory={setExpandedHistory}
              onDeletePayment={(billId, paymentId) => deletePayment(billId, paymentId, data.month_key)}
              toggling={toggling}
              emptyLabel={
                filter !== "all" || searchTerm
                  ? `No bills match this filter${searchTerm ? ` or "${searchTerm}"` : ""}.`
                  : undefined
              }
            />

            {/* NEXT MONTH PREVIEW — appears below current month so it's an ongoing cycle */}
            {nextData && nextData.bills && nextData.bills.length > 0 && (
              <div className="pt-2">
                <div className="flex items-center gap-3 mt-6 mb-3">
                  <div className="h-px flex-1 bg-zinc-800" />
                  <p className="text-xs uppercase tracking-widest text-zinc-500 font-bold flex items-center gap-2">
                    <CalendarIcon className="w-3.5 h-3.5" />
                    Coming up · {nextData.month_label}
                  </p>
                  <div className="h-px flex-1 bg-zinc-800" />
                </div>
                <p className="text-[11px] text-zinc-500 text-center -mt-2 mb-3">
                  Next month&apos;s bills appear here. They stay below until {month_label} is fully paid.
                </p>
                <div className="opacity-90">
                  <ListView
                    bills={nextData.bills}
                    monthKey={nextData.month_key}
                    monthLabel={nextData.month_label}
                    bulkMode={false}
                    selected={new Set()}
                    toggleSelect={() => {}}
                    onToggle={(b) => toggle(b, nextData.month_key)}
                    onPay={(b) => setPaymentFor({ bill: b, monthKey: nextData.month_key, amount: Math.max(0, b.amount - b.status.paid_total).toFixed(2), notes: "" })}
                    onEdit={(b) => { setEditing(b); setShowAddModal(true); }}
                    onDelete={deleteBill}
                    expandedHistory={expandedHistory}
                    setExpandedHistory={setExpandedHistory}
                    onDeletePayment={(billId, paymentId) => deletePayment(billId, paymentId, nextData.month_key)}
                    nextMonth
                  />
                </div>
              </div>
            )}
          </>
        ) : (
          <CalendarView
            bills={bills}
            monthKey={data.month_key}
            onToggle={(b) => toggle(b, data.month_key)}
            onEdit={(b) => { setEditing(b); setShowAddModal(true); }}
          />
        )}

        {/* Footer note */}
        <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-4 flex items-start gap-3 text-sm">
          <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
          <div className="text-zinc-300">
            <p className="font-semibold mb-1">Grace-period email reminders</p>
            <p className="text-xs text-zinc-400">
              Each bill has a 10-day grace period. You&apos;ll get an email at <strong>clearallcreditrepair@gmail.com</strong> on three days:
              the <strong>due day</strong> (&ldquo;Due TODAY&rdquo;), <strong>1 day before grace ends</strong> (&ldquo;Grace ends TOMORROW&rdquo;),
              and the <strong>day grace expires</strong> (&ldquo;LATE — penalty starts&rdquo;). Reminders run daily at 9 AM Pacific.
              Tap <em>Test email</em> to send yourself one right now.
            </p>
          </div>
        </div>
      </div>

      {/* Add / Edit modal */}
      {showAddModal && (
        <BillModal existing={editing} onCancel={() => { setShowAddModal(false); setEditing(null); }} onSave={saveBill} />
      )}

      {/* Payment modal */}
      {paymentFor && (
        <PaymentModal state={paymentFor} setState={setPaymentFor} onSubmit={submitPayment} onClose={() => setPaymentFor(null)} />
      )}
    </div>
  );
}

// ─── HeroPanel (Feb 2026 revamp v2) ───────────────────────────────────────────
// Big, bold, glance-worthy month header:
// • Left: month name, remaining balance headline, quick actions
// • Right: SVG circular progress ring (0-100 %)
// • Bottom: 4 mini stat pills (due / paid / remaining / bills)
function HeroPanel({ monthLabel, summary, bills, onForceAdvance }) {
  const totalDue = Number(summary.total_due) || 0;
  const totalPaid = Number(summary.total_paid) || 0;
  const remaining = Math.max(0, totalDue - totalPaid);
  const percent = totalDue > 0 ? Math.min(100, Math.round((totalPaid / totalDue) * 100)) : 0;
  const allDone = summary.all_paid;

  // SVG ring math
  const size = 128;
  const stroke = 12;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const dash = (percent / 100) * c;

  // Nearest upcoming unpaid bill
  const nextUnpaid = bills
    .filter((b) => !b.status?.paid_in_full)
    .sort((a, b) => (a.day || 99) - (b.day || 99))[0];

  return (
    <div
      className={`relative overflow-hidden rounded-3xl border p-5 md:p-7 backdrop-blur ${
        allDone
          ? "border-emerald-500/50 bg-gradient-to-br from-emerald-950/70 via-emerald-900/30 to-teal-950/50 shadow-2xl shadow-emerald-900/30"
          : "border-zinc-800/80 bg-gradient-to-br from-zinc-900/80 via-zinc-900/40 to-slate-950/60 shadow-xl"
      }`}
      data-testid="mb-hero"
    >
      {/* Decorative glow */}
      <div className="pointer-events-none absolute -top-24 -right-24 w-64 h-64 rounded-full bg-emerald-500/10 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-24 -left-24 w-64 h-64 rounded-full bg-teal-500/10 blur-3xl" />

      <div className="relative flex items-start justify-between gap-5 flex-wrap">
        <div className="flex-1 min-w-[220px]">
          <div className="flex items-center gap-2">
            <p className="text-[11px] text-emerald-400 uppercase tracking-[0.2em] font-bold">Monthly Bills</p>
            <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-full bg-zinc-800 text-zinc-400 border border-zinc-700">
              {new Date().toLocaleDateString(undefined, { month: "short", day: "numeric" })}
            </span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-black mt-1 tracking-tight" data-testid="month-label">
            {monthLabel}
          </h1>
          {allDone ? (
            <div className="mt-3 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-emerald-300" />
              <p className="text-lg font-semibold text-emerald-200">All bills paid — beautifully done.</p>
            </div>
          ) : (
            <>
              <div className="mt-2 flex items-baseline gap-2 flex-wrap">
                <p className="text-3xl md:text-4xl font-black text-white tabular-nums">
                  {fmt(remaining)}
                </p>
                <span className="text-sm text-zinc-400">left this month</span>
              </div>
              {nextUnpaid && (
                <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-zinc-900/60 border border-zinc-700/70 text-xs">
                  <Zap className="w-3.5 h-3.5 text-amber-400" />
                  <span className="text-zinc-400">Next up:</span>
                  <span className="font-semibold text-white truncate max-w-[180px]">{nextUnpaid.name}</span>
                  <span className="text-zinc-500">·</span>
                  <span className="text-amber-300 font-mono">
                    {fmt(Math.max(0, nextUnpaid.amount - (nextUnpaid.status?.paid_total || 0)))}
                  </span>
                  <span className="text-zinc-500">· Day {nextUnpaid.day}</span>
                </div>
              )}
            </>
          )}
          <button
            onClick={onForceAdvance}
            className="mt-4 text-[11px] text-zinc-500 hover:text-zinc-300 underline block"
            data-testid="mb-skip-month"
          >
            skip to next month
          </button>
        </div>

        {/* Circular progress ring */}
        <div className="relative flex-shrink-0" style={{ width: size, height: size }}>
          <svg width={size} height={size} className="-rotate-90">
            <circle
              cx={size / 2}
              cy={size / 2}
              r={r}
              fill="none"
              stroke="rgba(63,63,70,0.5)"
              strokeWidth={stroke}
            />
            <circle
              cx={size / 2}
              cy={size / 2}
              r={r}
              fill="none"
              stroke={allDone ? "url(#grad-done)" : "url(#grad-prog)"}
              strokeWidth={stroke}
              strokeLinecap="round"
              strokeDasharray={c}
              strokeDashoffset={c - dash}
              style={{ transition: "stroke-dashoffset 700ms ease" }}
            />
            <defs>
              <linearGradient id="grad-prog" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#06b6d4" />
                <stop offset="50%" stopColor="#3b82f6" />
                <stop offset="100%" stopColor="#10b981" />
              </linearGradient>
              <linearGradient id="grad-done" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#10b981" />
                <stop offset="100%" stopColor="#5eead4" />
              </linearGradient>
            </defs>
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className={`text-3xl font-black tabular-nums ${allDone ? "text-emerald-300" : "text-white"}`}>
              {percent}<span className="text-lg align-top">%</span>
            </span>
            <span className="text-[9px] uppercase tracking-widest text-zinc-500 font-bold">
              {summary.paid_count}/{summary.total_bills} paid
            </span>
          </div>
        </div>
      </div>

      {/* Stat pills */}
      <div className="relative grid grid-cols-2 sm:grid-cols-4 gap-2.5 mt-5">
        <StatPill icon={<Wallet className="w-3.5 h-3.5" />} label="Total due" value={fmt(totalDue)} tone="zinc" />
        <StatPill icon={<CheckCircle2 className="w-3.5 h-3.5" />} label="Paid" value={fmt(totalPaid)} tone="emerald" />
        <StatPill icon={<TrendingUp className="w-3.5 h-3.5" />} label="Remaining" value={fmt(remaining)} tone="amber" />
        <StatPill icon={<Layers className="w-3.5 h-3.5" />} label="Bills" value={`${summary.paid_count}/${summary.total_bills}`} tone="teal" />
      </div>
    </div>
  );
}

function StatPill({ icon, label, value, tone = "zinc" }) {
  const tones = {
    zinc: "border-zinc-700/60 text-zinc-200 bg-zinc-900/60",
    emerald: "border-emerald-500/40 text-emerald-200 bg-emerald-500/10",
    amber: "border-amber-500/40 text-amber-200 bg-amber-500/10",
    teal: "border-teal-500/40 text-teal-200 bg-teal-500/10",
  };
  return (
    <div className={`rounded-xl border px-3 py-2.5 flex items-center gap-2.5 ${tones[tone]} backdrop-blur-sm`}>
      <div className="opacity-80">{icon}</div>
      <div className="min-w-0">
        <div className="text-[9px] uppercase tracking-[0.15em] font-bold text-zinc-400 truncate">{label}</div>
        <div className="text-base sm:text-lg font-bold tabular-nums truncate">{value}</div>
      </div>
    </div>
  );
}

function FilterChip({ active, onClick, icon, label, count, testId, tone = "zinc" }) {
  const activeTones = {
    zinc: "bg-zinc-700 text-white border-zinc-500",
    emerald: "bg-emerald-500/25 text-emerald-200 border-emerald-500/60",
    amber: "bg-amber-500/20 text-amber-200 border-amber-500/50",
    red: "bg-red-500/20 text-red-200 border-red-500/50",
  };
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
        active
          ? `${activeTones[tone]} shadow-md`
          : "bg-zinc-900/60 text-zinc-400 border-zinc-800 hover:bg-zinc-800 hover:text-zinc-200"
      }`}
      data-testid={testId}
    >
      {icon}
      {label}
      <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-mono ${active ? "bg-black/25" : "bg-zinc-800/80 text-zinc-500"}`}>
        {count}
      </span>
    </button>
  );
}

// ─── MonthSummaryHero (legacy — kept for reference, unused after Feb 2026 revamp) ─
function MonthSummaryHero({ bills, monthLabel }) {
  if (!bills || bills.length === 0) return null;
  const total = bills.length;
  const paidBills = bills.filter((b) => b.status?.paid_in_full).length;
  const totalDue = bills.reduce((s, b) => s + (Number(b.amount) || 0), 0);
  const totalPaid = bills.reduce((s, b) => s + (Number(b.status?.paid_total) || 0), 0);
  const percentPaid = totalDue > 0 ? Math.min(100, Math.round((totalPaid / totalDue) * 100)) : 0;
  const remaining = Math.max(0, totalDue - totalPaid);
  // Find the next unpaid bill sorted by day-of-month.
  const nextUnpaid = bills
    .filter((b) => !b.status?.paid_in_full)
    .sort((a, b) => (a.day || 99) - (b.day || 99))[0];

  const fmt = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const allDone = paidBills === total;

  return (
    <div
      className={`rounded-2xl border p-4 md:p-5 mb-3 backdrop-blur transition-all ${
        allDone
          ? 'bg-gradient-to-br from-emerald-950/60 via-emerald-900/30 to-teal-950/40 border-emerald-500/40 shadow-lg shadow-emerald-900/20'
          : 'bg-gradient-to-br from-zinc-900/80 via-zinc-900/40 to-slate-950/60 border-zinc-800/80'
      }`}
      data-testid="mb-month-hero"
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-0.5">
            {monthLabel} · Progress
          </div>
          <div className={`text-2xl font-bold ${allDone ? 'text-emerald-300' : 'text-white'}`}>
            {allDone ? '🎉 All bills paid!' : `${fmt(remaining)} left`}
          </div>
          <div className="text-[11px] text-zinc-400 mt-0.5">
            {paidBills} of {total} bill{total === 1 ? '' : 's'} · {fmt(totalPaid)} paid of {fmt(totalDue)}
          </div>
        </div>
        <div className="text-right flex-shrink-0">
          <div className={`text-4xl md:text-5xl font-black tabular-nums ${
            allDone ? 'text-emerald-300' : percentPaid >= 66 ? 'text-emerald-400' : percentPaid >= 33 ? 'text-amber-400' : 'text-zinc-300'
          }`}>
            {percentPaid}<span className="text-xl align-top">%</span>
          </div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mt-0.5">Complete</div>
        </div>
      </div>
      {/* Progress bar */}
      <div className="h-2 rounded-full bg-zinc-800 overflow-hidden mb-1" aria-label={`${percentPaid}% paid`}>
        <div
          className={`h-full rounded-full transition-all duration-700 ${
            allDone
              ? 'bg-gradient-to-r from-emerald-500 via-emerald-400 to-teal-400'
              : 'bg-gradient-to-r from-cyan-500 via-blue-500 to-emerald-500'
          }`}
          style={{ width: `${percentPaid}%` }}
        />
      </div>
      {nextUnpaid && !allDone && (
        <div className="mt-3 flex items-center gap-2 text-[12px] text-zinc-300 border-t border-zinc-800/60 pt-3">
          <span className="text-zinc-500 uppercase tracking-wider text-[10px] font-bold">Next up:</span>
          <span className="font-semibold text-white">{nextUnpaid.name}</span>
          <span className="text-zinc-500">·</span>
          <span className="text-amber-300 font-mono">{fmt(Math.max(0, nextUnpaid.amount - nextUnpaid.status.paid_total))}</span>
          {nextUnpaid.day && (
            <>
              <span className="text-zinc-500">·</span>
              <span className="text-zinc-400">Due day {nextUnpaid.day}</span>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ─── List view ───────────────────────────────────────────────────────────────
function ListView({ bills, monthKey, monthLabel, bulkMode, selected, toggleSelect, onToggle, onPay, onEdit, onDelete, expandedHistory, setExpandedHistory, onDeletePayment, nextMonth = false, historical = false, toggling = new Set(), emptyLabel }) {
  if (bills.length === 0) {
    return (
      <div className="text-center py-14 rounded-2xl border border-dashed border-zinc-800 bg-zinc-900/30" data-testid={`bills-empty-${monthKey}`}>
        <div className="w-14 h-14 mx-auto rounded-full bg-emerald-500/10 flex items-center justify-center mb-3">
          <Coins className="w-6 h-6 text-emerald-400" />
        </div>
        <p className="text-zinc-400 text-sm">
          {emptyLabel || (
            <>
              No bills yet. Hit <strong className="text-emerald-300">Add bill</strong> to start.
            </>
          )}
        </p>
      </div>
    );
  }
  return (
    <div className="space-y-2">
      {bills.map((b) => {
        const st = b.status;
        const remaining = Math.max(0, b.amount - st.paid_total);
        const isPaid = st.paid_in_full;
        const hasPartial = !isPaid && st.paid_total > 0;
        const isHistoryOpen = !!expandedHistory[`${monthKey}-${b.id}`];
        const cat = CATEGORIES[b.category] || CATEGORIES.other;
        // Suppress "Due in / Overdue" chips on past or future months — only the
        // current month should show grace-period countdowns.
        const chip = nextMonth || historical ? null : statusChip(b);
        const isSel = selected.has(b.id);
        return (
          <div
            key={`${monthKey}-${b.id}`}
            className={`rounded-xl border transition-colors ${
              isPaid ? "border-emerald-500/40 bg-emerald-500/10"
              : hasPartial ? "border-amber-500/30 bg-amber-500/5"
              : nextMonth ? "border-zinc-800/70 bg-zinc-900/30"
              : "border-zinc-800 bg-zinc-900/60"
            }`}
            data-testid={`bill-row-${monthKey}-${b.id}`}
          >
            <div className="flex items-center gap-3 px-3 sm:px-4 py-3">
              {/* Bulk checkbox OR paid toggle */}
              {bulkMode ? (
                <button
                  onClick={() => toggleSelect(b.id)}
                  className="w-10 h-10 rounded-lg border-2 flex items-center justify-center flex-shrink-0 transition-all"
                  data-testid={`bulk-select-${b.id}`}
                >
                  {isSel ? <CheckSquare className="w-5 h-5 text-emerald-400" /> : <Square className="w-5 h-5 text-zinc-500" />}
                </button>
              ) : (
                <button
                  type="button"
                  disabled={toggling.has(b.id)}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onToggle(b);
                  }}
                  className={`w-11 h-11 md:w-12 md:h-12 rounded-full border-2 flex items-center justify-center transition-all flex-shrink-0 active:scale-90 disabled:opacity-70 ${
                    isPaid
                      ? "border-emerald-400 bg-emerald-500 text-white shadow-lg shadow-emerald-500/30 hover:bg-emerald-400 hover:border-emerald-300"
                      : "border-zinc-500 hover:border-emerald-400 hover:bg-emerald-500/15 hover:shadow-md hover:shadow-emerald-500/20"
                  }`}
                  title={isPaid ? "Tap to mark unpaid" : "Tap to mark paid in full"}
                  aria-label={isPaid ? `Mark ${b.name} unpaid` : `Mark ${b.name} paid`}
                  data-testid={`toggle-paid-${b.id}`}
                >
                  {toggling.has(b.id) ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : isPaid ? (
                    <Check className="w-6 h-6" strokeWidth={3} />
                  ) : null}
                </button>
              )}

              {/* Day badge */}
              <div className="w-12 flex-shrink-0 text-center hidden sm:block">
                <div className="text-[10px] text-zinc-500 uppercase">Due</div>
                <div className={`text-lg font-bold ${isPaid ? "text-emerald-300 line-through" : "text-zinc-200"}`}>{ordinal(b.day)}</div>
              </div>

              {/* Name + amount + status chip + category dot */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`w-2 h-2 rounded-full ${cat.dot}`} title={cat.label} />
                  <span className={`font-semibold truncate ${isPaid ? "line-through text-emerald-300" : ""}`}>{b.name}</span>
                  <span className="text-xs text-zinc-500 sm:hidden">due {ordinal(b.day)}</span>
                  {chip && (
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${chip.cls}`}>
                      <chip.icon className="w-3 h-3" /> {chip.label}
                    </span>
                  )}
                  {isPaid && (() => {
                    // Logged paid date: prefer paid_full_at; fall back to the latest payment date.
                    const lastPay = (st.payments && st.payments.length)
                      ? st.payments[st.payments.length - 1].date
                      : null;
                    const when = st.paid_full_at || lastPay;
                    if (!when) return null;
                    const d = new Date(when);
                    const label = d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
                    return (
                      <span
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border bg-emerald-500/15 text-emerald-300 border-emerald-500/40"
                        title={`Marked paid on ${d.toLocaleString()}`}
                        data-testid={`paid-on-${b.id}`}
                      >
                        <CheckCircle2 className="w-3 h-3" /> Paid {label}
                      </span>
                    );
                  })()}
                  {nextMonth && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border bg-zinc-800/60 text-zinc-400 border-zinc-700">
                      Next · {monthLabel}
                    </span>
                  )}
                  {historical && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border bg-zinc-800/60 text-zinc-400 border-zinc-700">
                      <CalendarIcon className="w-3 h-3" /> {monthLabel}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-0.5 text-sm flex-wrap">
                  <span className={`font-mono ${isPaid ? "text-emerald-300" : "text-zinc-300"}`}>{fmt(b.amount)}</span>
                  {hasPartial && <span className="text-amber-300 text-xs">• paid {fmt(st.paid_total)} • {fmt(remaining)} left</span>}
                  <span className={`text-[10px] ${cat.text}`}>{cat.label}</span>
                  {b.notes && <span className="text-zinc-500 text-xs">• {b.notes}</span>}
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-1 flex-shrink-0">
                {/* Explicit Mark Unpaid button (visible on paid rows so the action is obvious) */}
                {isPaid && (
                  <button
                    onClick={() => onToggle(b)}
                    className="px-2 py-1.5 text-xs rounded-md border border-red-500/40 bg-red-500/10 hover:bg-red-500/20 text-red-300 flex items-center gap-1"
                    title="Undo — mark this bill as unpaid"
                    data-testid={`mark-unpaid-${b.id}`}
                  >
                    <X className="w-3 h-3" /> <span className="hidden sm:inline">Unpaid</span>
                  </button>
                )}
                {b.pay_url && (
                  <a
                    href={b.pay_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-2 py-1.5 text-xs rounded-md bg-blue-600/80 hover:bg-blue-600 text-white flex items-center gap-1"
                    title="Open lender's payment page"
                    data-testid={`pay-url-${b.id}`}
                  >
                    <ExternalLink className="w-3 h-3" /> <span className="hidden sm:inline">Pay site</span>
                  </a>
                )}
                {!isPaid && (
                  <button onClick={() => onPay(b)} className="px-2 py-1.5 text-xs rounded-md bg-zinc-800 hover:bg-zinc-700 text-zinc-200 flex items-center gap-1" title="Add partial payment" data-testid={`add-payment-${b.id}`}>
                    <DollarSign className="w-3 h-3" /> <span className="hidden sm:inline">Payment</span>
                  </button>
                )}
                <button onClick={() => onEdit(b)} className="p-1.5 rounded-md text-zinc-400 hover:text-white hover:bg-zinc-800" title="Edit bill" data-testid={`edit-bill-${b.id}`}>
                  <Edit2 className="w-4 h-4" />
                </button>
                <button onClick={() => onDelete(b)} className="p-1.5 rounded-md text-zinc-500 hover:text-red-400 hover:bg-zinc-800" title="Remove bill" data-testid={`delete-bill-${b.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
                {st.payments && st.payments.length > 0 && (
                  <button onClick={() => setExpandedHistory((p) => ({ ...p, [`${monthKey}-${b.id}`]: !p[`${monthKey}-${b.id}`] }))} className="p-1.5 rounded-md text-zinc-400 hover:text-white hover:bg-zinc-800" title="Payment history">
                    {isHistoryOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>
                )}
              </div>
            </div>

            {/* Progress bar */}
            {!isPaid && b.amount > 0 && (
              <div className="px-4 pb-2">
                <div className="w-full bg-zinc-800 rounded-full h-1.5 overflow-hidden">
                  <div className={`h-1.5 transition-all ${hasPartial ? "bg-amber-400" : "bg-zinc-600"}`} style={{ width: `${Math.min(100, (st.paid_total / b.amount) * 100)}%` }} />
                </div>
              </div>
            )}

            {/* Payment history */}
            {isHistoryOpen && st.payments && st.payments.length > 0 && (
              <div className="border-t border-zinc-800 px-4 py-3 space-y-1.5">
                <p className="text-[11px] text-zinc-500 uppercase tracking-wider">Payment history</p>
                {st.payments.map((p) => (
                  <div key={p.id} className="flex items-center gap-2 text-sm">
                    <CalendarIcon className="w-3 h-3 text-zinc-500" />
                    <span className="text-zinc-300 font-mono w-20">{fmt(p.amount)}</span>
                    <span className="text-zinc-500 text-xs">{new Date(p.date).toLocaleDateString()}</span>
                    {p.notes && <span className="text-zinc-400 text-xs truncate">— {p.notes}</span>}
                    <div className="flex-1" />
                    <button onClick={() => onDeletePayment(b.id, p.id)} className="p-1 rounded text-zinc-500 hover:text-red-400" title="Remove">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Calendar view ───────────────────────────────────────────────────────────
function CalendarView({ bills, monthKey, onToggle, onEdit }) {
  const [y, m] = monthKey.split("-").map(Number);
  const firstDow = new Date(y, m - 1, 1).getDay(); // 0=Sun
  const daysInMonth = new Date(y, m, 0).getDate();
  const billsByDay = useMemo(() => {
    const map = {};
    for (const b of bills) {
      (map[b.day] = map[b.day] || []).push(b);
    }
    return map;
  }, [bills]);

  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const today = new Date();
  const isCurrentMonth = today.getFullYear() === y && today.getMonth() === m - 1;
  const todayDay = isCurrentMonth ? today.getDate() : -1;

  return (
    <div className="rounded-2xl border border-zinc-800 bg-zinc-900/60 p-3 sm:p-5 overflow-x-auto">
      <div className="grid grid-cols-7 gap-1 sm:gap-2 min-w-[560px]">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
          <div key={d} className="text-center text-[10px] sm:text-xs text-zinc-500 font-bold uppercase tracking-wider py-1">{d}</div>
        ))}
        {cells.map((d, i) => {
          if (d === null) return <div key={`b-${i}`} className="aspect-square" />;
          const dayBills = billsByDay[d] || [];
          const isToday = d === todayDay;
          return (
            <div
              key={d}
              className={`relative aspect-square rounded-lg border p-1 sm:p-1.5 flex flex-col text-[10px] sm:text-xs ${
                isToday ? "border-emerald-500 bg-emerald-500/10" : "border-zinc-800 bg-zinc-900/40"
              }`}
              data-testid={`cal-day-${d}`}
            >
              <span className={`font-bold ${isToday ? "text-emerald-300" : "text-zinc-400"}`}>{d}</span>
              <div className="flex flex-col gap-0.5 mt-0.5 overflow-hidden">
                {dayBills.map((b) => {
                  const cat = CATEGORIES[b.category] || CATEGORIES.other;
                  const isPaid = b.status.paid_in_full;
                  return (
                    <button
                      key={b.id}
                      onClick={() => onEdit(b)}
                      title={`${b.name} ${fmt(b.amount)}`}
                      className={`flex items-center gap-1 px-1 py-0.5 rounded text-left truncate text-[9px] sm:text-[10px] hover:ring-1 hover:ring-emerald-400 ${
                        isPaid ? "bg-emerald-500/30 text-emerald-200 line-through" : `${cat.glow} ${cat.text}`
                      }`}
                    >
                      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${cat.dot}`} />
                      <span className="truncate">{b.name.split(" ")[0]}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
      <p className="text-[10px] text-zinc-500 mt-3 text-center">Tap a bill block to edit • Today highlighted in green</p>
    </div>
  );
}

// ─── Tiny components ─────────────────────────────────────────────────────────
function Stat({ label, value, accent = "zinc" }) {
  const accents = {
    zinc: "border-zinc-700 text-zinc-200",
    emerald: "border-emerald-500/40 text-emerald-300",
    amber: "border-amber-500/40 text-amber-300",
    teal: "border-teal-500/40 text-teal-300",
  };
  return (
    <div className={`rounded-xl border bg-zinc-900/40 px-4 py-3 ${accents[accent]}`}>
      <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">{label}</p>
      <p className="text-lg sm:text-xl font-bold mt-1">{value}</p>
    </div>
  );
}

function BillModal({ existing, onCancel, onSave }) {
  const [name, setName] = useState(existing?.name || "");
  const [day, setDay] = useState(existing?.day || 1);
  const [amount, setAmount] = useState(existing?.amount ?? 0);
  const [notes, setNotes] = useState(existing?.notes || "");
  const [category, setCategory] = useState(existing?.category || "other");
  const [payUrl, setPayUrl] = useState(existing?.pay_url || "");

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <div className="bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-md p-5 space-y-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold">{existing ? "Edit Bill" : "Add Bill"}</h3>
          <button onClick={onCancel} className="text-zinc-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <div>
          <label className="text-xs text-zinc-400">Name</label>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Lucid Auto" className="mt-1 bg-zinc-800 border-zinc-700" data-testid="bill-name-input" />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-zinc-400">Day of month</label>
            <Input type="number" min={1} max={31} value={day} onChange={(e) => setDay(parseInt(e.target.value || "1", 10))} className="mt-1 bg-zinc-800 border-zinc-700" data-testid="bill-day-input" />
          </div>
          <div>
            <label className="text-xs text-zinc-400">Amount ($)</label>
            <Input type="number" step="0.01" min={0} value={amount} onChange={(e) => setAmount(parseFloat(e.target.value || "0"))} className="mt-1 bg-zinc-800 border-zinc-700" data-testid="bill-amount-input" />
          </div>
        </div>

        <div>
          <label className="text-xs text-zinc-400">Category</label>
          <div className="grid grid-cols-5 gap-1 mt-1">
            {Object.entries(CATEGORIES).map(([key, c]) => (
              <button
                key={key}
                type="button"
                onClick={() => setCategory(key)}
                className={`px-2 py-2 rounded-lg border text-[11px] font-medium transition-all ${
                  category === key ? `${c.border} ${c.glow} ${c.text}` : "border-zinc-700 text-zinc-500 hover:text-zinc-300"
                }`}
              >
                <span className={`inline-block w-2 h-2 rounded-full mr-1 ${c.dot}`} />
                {c.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="text-xs text-zinc-400">Pay-site URL (optional)</label>
          <Input value={payUrl} onChange={(e) => setPayUrl(e.target.value)} placeholder="https://lender.com/login" className="mt-1 bg-zinc-800 border-zinc-700" data-testid="bill-payurl-input" />
        </div>

        <div>
          <label className="text-xs text-zinc-400">Notes (optional)</label>
          <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Account #, autopay, anything..." className="mt-1 bg-zinc-800 border-zinc-700" />
        </div>

        <div className="flex gap-2 pt-2">
          <Button variant="outline" onClick={onCancel} className="flex-1 bg-zinc-800 border-zinc-700 hover:bg-zinc-700">Cancel</Button>
          <Button
            onClick={() => {
              if (!name.trim()) { toast.error("Name is required"); return; }
              onSave({ name: name.trim(), day, amount, notes, category, pay_url: payUrl.trim() });
            }}
            className="flex-1 bg-emerald-600 hover:bg-emerald-700"
            data-testid="save-bill-btn"
          >
            {existing ? "Save changes" : "Add bill"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function PaymentModal({ state, setState, onSubmit, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <div className="bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-md p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold">Payment — {state.bill.name}</h3>
          <button onClick={onClose} className="text-zinc-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-xs text-zinc-400">
          Bill amount: <span className="text-zinc-200">{fmt(state.bill.amount)}</span> · Already paid: <span className="text-zinc-200">{fmt(state.bill.status.paid_total)}</span>
        </p>
        <div>
          <label className="text-xs text-zinc-400">Payment amount ($)</label>
          <Input type="number" step="0.01" min={0} value={state.amount} onChange={(e) => setState({ ...state, amount: e.target.value })} className="mt-1 bg-zinc-800 border-zinc-700" data-testid="payment-amount-input" />
        </div>
        <div>
          <label className="text-xs text-zinc-400">Notes (optional)</label>
          <Input value={state.notes} onChange={(e) => setState({ ...state, notes: e.target.value })} placeholder="confirmation #, method, etc." className="mt-1 bg-zinc-800 border-zinc-700" />
        </div>
        <div className="flex gap-2 pt-2">
          <Button variant="outline" onClick={onClose} className="flex-1 bg-zinc-800 border-zinc-700 hover:bg-zinc-700">Cancel</Button>
          <Button onClick={onSubmit} className="flex-1 bg-emerald-600 hover:bg-emerald-700" data-testid="submit-payment-btn">Save payment</Button>
        </div>
      </div>
    </div>
  );
}
